/**
 * Property Maintenance → Transfer list.
 * Search field on the toolbar; other filters in the slide-out drawer. Query fields match Angular
 * `transfer-process-list`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronsUpDown, Download, Filter, Loader2, Pencil } from 'lucide-react'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated, selectUser } from '@/features/auth/authSlice'
import { showApiError } from '@/lib/utils/notifications'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { ListingPagination } from '@/components/listing/ListingPagination'
import {
  ListingDataTableCard,
  } from '@/components/ui/listingDataTable'
import { cn } from '@/lib/utils/helpers'
import { useNavigate } from 'react-router-dom'
import parityStyles from '@/features/invitee/styles/invitesParity.module.css'
import configSortStyles from '@/components/listing/propertySortHeaderButton.module.css'
import { getTransfers, getTransferExportUrl } from '../services/transferService'
import { TransferListFilters } from '../components/TransferListFilters'
import { useUserRole } from '@/hooks/useUserRole'
import { TRANSFER_PER_PAGE_OPTIONS } from '../constants/transferListConstants'
import {
  DEFAULT_TRANSFER_FILTER_FORM,
  type TransferFilterFormValues,
} from '../types/transferFilterForm'
import { buildTransferListParamsFromForm } from '../utils/transferFilterBuild'
import { buildTransferWizardPathFromListRow } from '../utils/transferListNavigation'
import { formatTransferUpdatedAt } from '../utils/transferListFormatters'
import type { TransferListParams, TransferRecord } from '../types/transfer'

import {
  readTransferListSessionFilter,
  writeTransferListSessionFilter,
  clearTransferListSessionFilter,
  type SavedTransferSessionFilter,
} from '../utils/transferSessionFilter'

const DEFAULT_SORT: Pick<TransferListParams, 'perPage' | 'sortBy' | 'orderBy' | 'page'> = {
  page: 1,
  perPage: 15,
  sortBy: 'desc',
  orderBy: 'updated_at',
}

import type { TransferPageSortKey } from '../types'

function initialFormFromUser(user: ReturnType<typeof selectUser>): TransferFilterFormValues {
  const f: TransferFilterFormValues = { ...DEFAULT_TRANSFER_FILTER_FORM }
  if (!user?.current_role) return f
  if (user.current_role.org_id != null) f.org_id = String(user.current_role.org_id)
  if (user.current_role.company_id != null) f.company_id = String(user.current_role.company_id)
  if (user.current_role.property_id != null) f.property_id = String(user.current_role.property_id)
  return f
}

function applySessionToFormAndParams(
  baseForm: TransferFilterFormValues,
  session: SavedTransferSessionFilter | null,
): { form: TransferFilterFormValues; params: TransferListParams } {
  if (!session) {
    const params = buildTransferListParamsFromForm(baseForm, DEFAULT_SORT)
    return { form: baseForm, params }
  }

  let dateStart: Date | null = null
  let dateEnd: Date | null = null
  if (session.dateStartISO) {
    dateStart = new Date(session.dateStartISO)
  } else if (session.fromDt) {
    const parts = session.fromDt.split('-')
    if (parts.length === 3) {
      dateStart = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]))
    }
  }

  if (session.dateEndISO) {
    dateEnd = new Date(session.dateEndISO)
  } else if (session.toDt) {
    const parts = session.toDt.split('-')
    if (parts.length === 3) {
      dateEnd = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]))
    }
  }

  const form: TransferFilterFormValues = {
    ...baseForm,
    search: session.search ?? baseForm.search,
    org_id: baseForm.org_id || session.org_id || '',
    company_id: baseForm.company_id || session.company_id || '',
    property_id: baseForm.property_id || session.property_id || '',
    process_name: session.process_name ?? baseForm.process_name,
    fk_team_id: session.fk_team_id ?? baseForm.fk_team_id,
    status: session.status ?? baseForm.status,
    dateStart: dateStart || baseForm.dateStart,
    dateEnd: dateEnd || baseForm.dateEnd,
  }

  const sortOverrides = {
    page: session.page ?? DEFAULT_SORT.page,
    perPage: session.perPage ?? DEFAULT_SORT.perPage,
    sortBy: session.sortBy ?? DEFAULT_SORT.sortBy,
    orderBy: session.orderBy ?? DEFAULT_SORT.orderBy,
  }

  const params = buildTransferListParamsFromForm(form, sortOverrides)
  return { form, params }
}

export function TransferPage() {
  const navigate = useNavigate()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector(selectUser)
  const { orgId, companyId, propertyId } = useUserRole()
  const [records, setRecords] = useState<TransferRecord[]>([])
  const [pagination, setPagination] = useState({ current: 1, last: 1, total: 0 })
  const [appliedParams, setAppliedParams] = useState<TransferListParams | null>(null)
  const [filterForm, setFilterForm] = useState<TransferFilterFormValues>(DEFAULT_TRANSFER_FILTER_FORM)
  const [loading, setLoading] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const appliedRef = useRef<TransferListParams | null>(null)
  const filterFormRef = useRef(filterForm)
  const searchTermRef = useRef(searchTerm)
  const appliedParamsRef = useRef(appliedParams)
  filterFormRef.current = filterForm
  appliedRef.current = appliedParams
  searchTermRef.current = searchTerm
  appliedParamsRef.current = appliedParams

  const lockedOrgId = orgId != null ? String(orgId) : undefined
  const lockedCompanyId = companyId != null ? String(companyId) : undefined
  const lockedPropertyId = propertyId != null ? String(propertyId) : undefined

  const load = useCallback(async (next: TransferListParams) => {
    setLoading(true)
    try {
      const response = await getTransfers(next)
      setRecords(response.data ?? [])
      setPagination({
        current: response.current ?? 1,
        last: Math.max(1, response.last ?? 1),
        total: response.total ?? 0,
      })
      writeTransferListSessionFilter(filterFormRef.current, next)
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  /** Form → list API (page 1); shared by toolbar search, drawer filters, Enter key. */
  const commitListFromForm = useCallback(
    (next: TransferFilterFormValues) => {
      const p = appliedRef.current
      if (!p) return
      const built = buildTransferListParamsFromForm(next, {
        page: 1,
        perPage: p.perPage,
        sortBy: p.sortBy,
        orderBy: p.orderBy,
      })
      setFilterForm(next)
      setAppliedParams(built)
      void load(built)
    },
    [load],
  )

  const handleDrawerFilterChange = useCallback(
    (patch: Partial<TransferFilterFormValues>) => {
      setFilterForm((f) => {
        /** Toolbar text can be ahead of `f.search` until debounce runs — always merge. */
        const next = { ...f, ...patch, search: searchTermRef.current }
        const p = appliedRef.current
        if (p) {
          const built = buildTransferListParamsFromForm(next, {
            page: 1,
            perPage: p.perPage,
            sortBy: p.sortBy,
            orderBy: p.orderBy,
          })
          setAppliedParams(built)
          void load(built)
        }
        return next
      })
    },
    [load],
  )

  useEffect(() => {
    if (!isAuthenticated || !user) return
    const baseForm = initialFormFromUser(user)
    const session = readTransferListSessionFilter()
    const { form, params } = applySessionToFormAndParams(baseForm, session)

    setFilterForm(form)
    setSearchTerm(form.search)
    setAppliedParams(params)
    void load(params)
  }, [isAuthenticated, user?.user_id, load])

  /** Debounced search — compare to last *applied* `search` (ref). */
  useEffect(() => {
    const t = setTimeout(() => {
      const current = appliedParamsRef.current
      if (!current) return
      const trimmed = searchTerm.trim()
      const applied = (current.search ?? '').trim()
      if (trimmed === applied) return
      commitListFromForm({ ...filterFormRef.current, search: searchTerm })
    }, 500)
    return () => clearTimeout(t)
  }, [searchTerm, commitListFromForm])

  const resetFilters = () => {
    clearTransferListSessionFilter()
    const p = appliedRef.current
    if (!p) return
    const form = user ? initialFormFromUser(user) : { ...DEFAULT_TRANSFER_FILTER_FORM }
    setFilterForm(form)
    setSearchTerm(form.search)
    const next = buildTransferListParamsFromForm(form, {
      page: 1,
      perPage: p.perPage,
      sortBy: p.sortBy,
      orderBy: p.orderBy,
    })
    setAppliedParams(next)
    void load(next)
  }

  /** Drawer-only filters (search is on the toolbar and not included in the Filters badge). */
  const activeFilterCount = useMemo(() => {
    let n = 0
    if (filterForm.org_id && filterForm.org_id !== lockedOrgId) n += 1
    if (filterForm.company_id && filterForm.company_id !== lockedCompanyId) n += 1
    if (filterForm.property_id && filterForm.property_id !== lockedPropertyId) n += 1
    if (filterForm.process_name) n += 1
    if (filterForm.fk_team_id) n += 1
    if (filterForm.dateStart && filterForm.dateEnd) n += 1
    if (filterForm.status && filterForm.status !== DEFAULT_TRANSFER_FILTER_FORM.status) n += 1
    return n
  }, [filterForm, lockedOrgId, lockedCompanyId, lockedPropertyId])

  const exportUrl = useMemo(() => {
    if (!appliedParams) return null
    return getTransferExportUrl(appliedParams)
  }, [appliedParams])

  const goToPage = (pageNo: number) => {
    const p = appliedRef.current
    if (!p) return
    const next = { ...p, page: pageNo }
    setAppliedParams(next)
    void load(next)
  }

  const changePerPage = (perPage: number) => {
    const p = appliedRef.current
    if (!p) return
    const next = { ...p, perPage, page: 1 }
    setAppliedParams(next)
    void load(next)
  }

  /** Angular `sort()`: toggles `sortBy` on every header click, then sets `orderBy`. */
  const toggleSort = (key: TransferPageSortKey) => {
    const p = appliedRef.current
    if (!p) return
    const nextDir: 'asc' | 'desc' = p.sortBy === 'asc' ? 'desc' : 'asc'
    const next = { ...p, orderBy: key, sortBy: nextDir, page: 1 }
    setAppliedParams(next)
    void load(next)
  }

  const SortIndicator = ({ column }: { column: TransferPageSortKey }) => {
    const isActive = appliedParams?.orderBy === column
    return (
      <span
        aria-hidden="true"
        className={cn(parityStyles.sortIcon, isActive && parityStyles.sortIconActive)}
      >
        {isActive ? (appliedParams?.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3" />}
      </span>
    )
  }

  if (!appliedParams) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <PageHeader title="TRANSFER LIST" />
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-button" />
        </div>
      </div>
    )
  }

  const pageOptions = {
    current: pagination.current,
    last: pagination.last,
    total: pagination.total,
  }
  const currentPage = pageOptions.current || 1
  const totalPages = pageOptions.last || 1

  return (
    <div
      className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 pb-12"
      data-testid="transfer-list-page"
    >
      <PageHeader title="TRANSFER LIST" />

      <div
        className="mx-auto flex w-full flex-col gap-4 px-3 py-4 sm:gap-6 sm:px-6 sm:py-8"
        data-testid="transfer-list-main"
      >
        <div
          className="flex flex-col items-center justify-between gap-4 md:flex-row"
          data-testid="transfer-list-toolbar"
        >
          {/* Same search bar as InviteeList (toolbar) — wide input, no outer card */}
          <div
            data-testid="transfer-list-search-wrap"
            className="relative w-full max-w-lg md:w-1/2"
          >
            <div className="absolute top-1/2 left-4 -translate-y-1/2 text-slate-400" aria-hidden>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </div>
            <input
              type="text"
              data-testid="transfer-list-search-input"
              placeholder="Search transfers..."
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pr-4 pl-12 text-sm shadow-sm transition-all placeholder:text-slate-400 focus:border-button focus:ring-2 focus:ring-button/20 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                e.preventDefault()
                commitListFromForm({ ...filterFormRef.current, search: searchTerm })
              }}
              autoComplete="off"
            />
          </div>

          <div
            className="flex w-full items-center gap-3 md:w-auto"
            data-testid="transfer-list-toolbar-actions"
          >
            <button
              type="button"
              data-testid="transfer-list-filters-open-button"
              onClick={() => setIsFilterOpen(true)}
              className={`relative flex flex-1 items-center justify-center gap-2 rounded-xl border px-5 py-2.5 text-sm font-semibold whitespace-nowrap shadow-sm transition-all hover:shadow-md md:flex-none ${
                activeFilterCount > 0
                  ? 'border-button text-button-hover'
                  : 'border-slate-200 text-slate-700'
              } bg-white hover:bg-slate-50`}
            >
              <Filter className="h-[18px] w-[18px] text-slate-600" aria-hidden />
              Filters
              {activeFilterCount > 0 && (
                <span
                  data-testid="transfer-list-filters-active-count-badge"
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-button text-xs leading-none font-bold text-white"
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
            {exportUrl && records.length > 0 && (
              <a
                href={exportUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="transfer-list-export-button"
                className="relative flex flex-1 items-center justify-center gap-2 rounded-xl border border-button bg-button px-5 py-2.5 text-sm font-semibold text-white whitespace-nowrap shadow-sm transition-all hover:bg-button-hover hover:shadow-md md:flex-none"
              >
                <Download className="h-[18px] w-[18px]" aria-hidden />
                Export
              </a>
            )}
          </div>
        </div>

        <ListingDataTableCard
          data-testid="transfer-list-results-card"
          className="flex flex-col"
        >
          {loading && (
            <div
              className="flex items-center justify-center py-20"
              data-testid="transfer-list-loading"
            >
              <Loader2 className="h-8 w-8 animate-spin text-button" />
            </div>
          )}

          {!loading && (
            <>
              <div
                className="flex flex-col items-stretch justify-between gap-3 border-b border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:px-6 sm:py-4"
                data-testid="transfer-list-pagination-header-bar"
              >
                <div className="text-center text-xs font-medium text-slate-500 sm:text-left sm:text-sm">
                  Page <span className="font-bold text-slate-900">{currentPage}</span> of{' '}
                  <span className="font-bold text-slate-900">{totalPages}</span>
                  <span className="mx-2 text-slate-300">|</span>
                  Total <span className="font-bold text-slate-900">{pagination.total}</span> Records
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
                  <ListingPagination
                    pageOptions={pageOptions}
                    onPageChange={goToPage}
                    placement="header"
                    dataTestIdPrefix="transfer-list"
                  />
                  <div className="ml-0 flex items-center gap-2 sm:ml-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Rows
                    </span>
                    <select
                      className="rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-700 focus:border-button focus:outline-none focus:ring-2 focus:ring-button/30"
                      value={appliedParams.perPage}
                      onChange={(e) => changePerPage(parseInt(e.target.value, 10))}
                      aria-label="Rows per page"
                    >
                      {TRANSFER_PER_PAGE_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className={cn(parityStyles.tableResponsiveScroll, 'touch-scroll')} data-testid="transfer-list-data-scroll">
                <table className={cn(parityStyles.listingTable, parityStyles.propertyListingTable, 'table-fixed w-full min-w-[70rem] !max-w-none [&_thead_th]:!whitespace-nowrap [&_tbody_td:not([colspan])]:!whitespace-normal [&_tbody_td:not([colspan])]:break-words [&_thead_th]:!px-2.5 [&_thead_th]:!py-2 [&_thead_th]:!align-middle [&_tbody_td]:!px-2.5 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[13px] [&_tbody_td]:!leading-snug')}>
                  <thead>
                    <tr>
                      <th className="select-none !text-left" onClick={() => toggleSort('property_name')}>
                        <button type="button" className={cn(parityStyles.sortHeaderButton, configSortStyles.propertySortHeaderButton, 'justify-start w-full')}>
                          <span className={parityStyles.sortHeaderLabel}>Name</span>
                          <SortIndicator column="property_name" />
                        </button>
                      </th>
                      <th className="select-none !text-left" onClick={() => toggleSort('team_name')}>
                        <button type="button" className={cn(parityStyles.sortHeaderButton, configSortStyles.propertySortHeaderButton, 'justify-start w-full')}>
                          <span className={parityStyles.sortHeaderLabel}>Team</span>
                          <SortIndicator column="team_name" />
                        </button>
                      </th>
                      <th className="select-none !text-left" onClick={() => toggleSort('process_name')}>
                        <button type="button" className={cn(parityStyles.sortHeaderButton, configSortStyles.propertySortHeaderButton, 'justify-start w-full')}>
                          <span className={parityStyles.sortHeaderLabel}>Process Name</span>
                          <SortIndicator column="process_name" />
                        </button>
                      </th>
                      <th className="select-none !text-left" onClick={() => toggleSort('status')}>
                        <button type="button" className={cn(parityStyles.sortHeaderButton, configSortStyles.propertySortHeaderButton, 'justify-start w-full')}>
                          <span className={parityStyles.sortHeaderLabel}>Status</span>
                          <SortIndicator column="status" />
                        </button>
                      </th>
                      <th className="select-none !text-left" onClick={() => toggleSort('updated_at')}>
                        <button type="button" className={cn(parityStyles.sortHeaderButton, configSortStyles.propertySortHeaderButton, 'justify-start w-full')}>
                          <span className={parityStyles.sortHeaderLabel}>Updated At</span>
                          <SortIndicator column="updated_at" />
                        </button>
                      </th>
                      <th className="select-none !text-left" onClick={() => toggleSort('added_by')}>
                        <button type="button" className={cn(parityStyles.sortHeaderButton, configSortStyles.propertySortHeaderButton, 'justify-start w-full')}>
                          <span className={parityStyles.sortHeaderLabel}>Added By</span>
                          <SortIndicator column="added_by" />
                        </button>
                      </th>
                      <th className="select-none !text-left" onClick={() => toggleSort('source')}>
                        <button type="button" className={cn(parityStyles.sortHeaderButton, configSortStyles.propertySortHeaderButton, 'justify-start w-full')}>
                          <span className={parityStyles.sortHeaderLabel}>Source</span>
                          <SortIndicator column="source" />
                        </button>
                      </th>
                      <th className="!text-left w-20">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="!text-center text-slate-500 py-10">
                          No transfers found.
                        </td>
                      </tr>
                    ) : (
                      records.map((row) => {
                        const transferEditPath = buildTransferWizardPathFromListRow(row)
                        const nameCell =
                          row.property_name || row.company_name
                            ? row.property_name || row.company_name
                            : '—'
                        return (
                          <tr key={row.id} className="transition-colors hover:bg-slate-50/50">
                            <td className="font-medium text-slate-900">{nameCell}</td>
                            <td className="text-slate-700">{row.team_name || '—'}</td>
                            <td className="text-slate-700">{row.process_name || '—'}</td>
                            <td>
                              <StatusBadge status={row.status} />
                            </td>
                            <td className="whitespace-nowrap text-slate-600">
                              {formatTransferUpdatedAt(row.updated_at) || '—'}
                            </td>
                            <td className="text-slate-700">{row.added_by || '—'}</td>
                            <td className="text-slate-700">{row.source || '—'}</td>
                            <td>
                              <button
                                type="button"
                                onClick={() => {
                                  if (transferEditPath) navigate(transferEditPath)
                                }}
                                disabled={!transferEditPath}
                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white p-2 text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                                title="Open transfer"
                                aria-label="Open transfer"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div
                className="flex flex-col items-stretch justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:px-6 sm:py-4"
                data-testid="transfer-list-pagination-footer-bar"
              >
                <div className="text-center text-xs font-medium text-slate-500 sm:text-left sm:text-sm">
                  Page <span className="font-bold text-slate-900">{currentPage}</span> of{' '}
                  <span className="font-bold text-slate-900">{totalPages}</span>
                  <span className="mx-2 text-slate-300">|</span>
                  Total <span className="font-bold text-slate-900">{pagination.total}</span> Records
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
                  <ListingPagination
                    pageOptions={pageOptions}
                    onPageChange={goToPage}
                    placement="footer"
                    dataTestIdPrefix="transfer-list"
                  />
                  <div className="ml-0 flex items-center gap-2 sm:ml-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Rows
                    </span>
                    <select
                      className="rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-700 focus:border-button focus:outline-none focus:ring-2 focus:ring-button/30"
                      value={appliedParams.perPage}
                      onChange={(e) => changePerPage(parseInt(e.target.value, 10))}
                      aria-label="Rows per page"
                    >
                      {TRANSFER_PER_PAGE_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
        </ListingDataTableCard>
      </div>

      <TransferListFilters
        form={filterForm}
        onFiltersChange={handleDrawerFilterChange}
        onReset={resetFilters}
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      />
    </div>
  )
}

function StatusBadge({ status }: { status?: string }) {
  if (!status) return <span className="text-slate-400">—</span>
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium ${matchPalette(status)}`}
    >
      {status}
    </span>
  )
}

function matchPalette(status: string): string {
  switch (status) {
    case 'Completed':
      return 'bg-green-100 text-green-700'
    case 'InProgress':
      return 'bg-button-soft text-button'
    case 'Cancelled':
      return 'bg-red-100 text-red-700'
    case 'Pending':
    default:
      return 'bg-amber-100 text-amber-700'
  }
}
